import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Card, Form, Badge, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AiFillEye, AiFillEyeInvisible } from 'react-icons/ai';
import { FaPlus } from 'react-icons/fa';
import { FiDownload } from 'react-icons/fi';
import { BsCreditCard } from 'react-icons/bs';
import preview from 'assets/img/preview.jpg';
import error from 'assets/img/static/ico_error404_xl.png';
import { BiCalendarCheck } from 'react-icons/bi';
import { PiBroomBold } from 'react-icons/pi';
import { formatCurrency } from 'utils/currencyFormatter';
import { toast } from 'react-toastify';
import { convertDateToDDMMYYYY } from 'helpers';
import { getProductStatus } from 'constants';
import { getSubTotalOrder, getTotalOrder, getIvaOrder } from 'utils/orderFunctions';
import { createFilesToExport } from 'utils/backoffice/exportPDFExcel';
// custom hooks
import useAssignedLibrary from 'utils/customHooks/useAssignedLibrary';
import { useUserStore } from 'stores/userStore';
// api
import { getMyOrdersWithLang, getDetailOrder, getMinYearOrder, createReturn, editDetailOrder } from 'api/orders';
import { getInfoRedsys } from 'api/ecommerce';
// components
import Head from 'components/metatags_helmet/metatags';
import DatePicker from 'components/backoffice/dateTimePicker';
import Pagination from 'components/userzone/Pagination';
import NoResults from 'components/userzone/NoResults';
import AddItemsToOrderModal from 'components/backoffice/modals/AddItemsToOrderModal';
import { formatOrderLineBookConditionDisplay, isBookOrderLine, orderLinesConditionColumnI18nKey } from 'utils/bookCondition';

/** Estados de pago del pedido que requieren completar el pago (API storehouse). */
const isPendingPaymentOrder = (order) => {
  const status = order?.payment_status;
  return (
    status === 'pendingPayment' ||
    status === 'notPaid' ||
    status === 'pending' ||
    status === 'pending_payment'
  );
};

/** TPV = tarjeta (id 4 en storehouse). La API puede devolver número, string o texto de método. */
const isTpvPaymentMethod = (order) => {
  const m = order?.payment_method;
  if (m === 4 || m === '4') return true;
  if (typeof m === 'string' && m.trim().toUpperCase() === 'TPV') return true;
  const display = order?.payment_method_display;
  if (typeof display === 'string' && display.trim().toUpperCase() === 'TPV') return true;
  return false;
};

const shouldShowPayOrderButton = (order, isConcourseUser = false) =>
  isPendingPaymentOrder(order) &&
  isTpvPaymentMethod(order) &&
  !isConcourseUser;

/** Para TPV: priorizar código visible del pedido; si no hay, usar id interno. */
const getOrderPaymentRef = (order) => {
  const code = order?.code;
  if (code != null && String(code).trim() !== '') return code;
  return order?.id;
};

const OrderList = () => {
  const { t, i18n } = useTranslation(['backoffice', 'home', 'header', 'payment', 'detalle']);
  const title = t('pedidos');
  const [orders, setOrders] = useState([]);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedYear, setSelectedYear] = useState(null);
  const [minYear, setMinYear] = useState(null);
  const maxYear = new Date().getFullYear();
  const [maxPage, setMaxPage] = useState(null);
  const [page, setPage] = useState(1);
  const [selectedArticles, setSelectedArticles] = useState([]);
  const [articleStatus, setArticleStatus] = useState([]);
  const [showAddItemsModal, setShowAddItemsModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingOrders, setIsLoadingOrders] = useState(true);
  const [exportingOrderId, setExportingOrderId] = useState(null);
  const [payingOrderId, setPayingOrderId] = useState(null);
  const { assignedLibrary, libraryPath } = useAssignedLibrary();
  const { username, email, isConcourseUser } = useUserStore();

  const formatArticleBookCondition = (item) =>
    formatOrderLineBookConditionDisplay(item, (k) => t(k, { ns: 'detalle' }));

  const submitRedsysForm = (redsys) => {
    if (!redsys?.Target_Url) return;
    if (!localStorage.getItem('historyRedsys')) {
      localStorage.setItem('historyRedsys', JSON.stringify(redsys.Target_Url.includes('redsys')));
    }
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = redsys.Target_Url;
    form.style.display = 'none';
    const fields = [
      ['Ds_SignatureVersion', redsys.Ds_SignatureVersion],
      ['Ds_MerchantParameters', redsys.Ds_MerchantParameters],
      ['Ds_Signature', redsys.Ds_Signature],
    ];
    fields.forEach(([name, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value ?? '';
      form.appendChild(input);
    });
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  };

  const handlePayOrder = async (order) => {
    setPayingOrderId(order.id);
    try {
      const ref = getOrderPaymentRef(order);
      const { status, data } = await getInfoRedsys({ order_id: ref });
      if (status === 200 && data?.Target_Url) {
        submitRedsysForm(data);
      } else {
        toast.error(t('mensajeerror'));
      }
    } catch (e) {
      console.error('getInfoRedsys pedido:', e);
      toast.error(t('mensajeerror'));
    } finally {
      setPayingOrderId(null);
    }
  };

  useEffect(() => {
    setArticleStatus(getProductStatus(t));
  }, [t]);

  const handleSelectArticle = (id) => {
    setSelectedArticles((prevSelected) =>
      prevSelected.includes(id) ? prevSelected.filter((articleId) => articleId !== id) : [...prevSelected, id],
    );
  };

  const getPriceWithDiscount = (precioVenta, descuento) => {
    const precio = parseFloat(precioVenta || 0);
    const desc = parseFloat(descuento || 0);
    
    if (desc > 0.00) {
      return precio * (1 - desc / 100);
    }
    
    return precio;
  };

  const renderPrice = (item) => {
    const descuento = parseFloat(item.descuento || 0);
    const precioOriginal = parseFloat(item.precio_venta || 0);
    const precioConDescuento = getPriceWithDiscount(item.precio_venta, item.descuento);
    const hasDiscount = descuento > 0.00;
    
    if (hasDiscount) {
      return (
        <>
          <span style={{ textDecoration: 'line-through', marginRight: '8px' }}>
            {formatCurrency(precioOriginal)}
          </span>
          <span style={{ marginRight: '8px' }}>→</span>
          <span className="price-text-desc">{formatCurrency(precioConDescuento)}</span>
        </>
      );
    }
    
    return formatCurrency(precioOriginal);
  };

  const fetchOrders = async () => {
    setIsLoadingOrders(true);
    try {
      const { data, status } = await getMyOrdersWithLang(
        {
          page,
          page_size: 10,
          year: selectedYear || '',
          search: username || email || '',
        },
        i18n.language,
      );
      if (status === 200 && Array.isArray(data?.results)) {
        setOrders(data?.results);
        setErrorMessage('');
        setMaxPage(data?.total_pages);
      } else {
        setErrorMessage(t('mensajeerror'));
      }
    } catch (err) {
      setErrorMessage(t('mensajeerror'));
    } finally {
      setIsLoadingOrders(false);
    }
  };

  const handleExportOrder = async (order) => {
    setExportingOrderId(order.id);
    try {
      const { data, status } = await getDetailOrder(order.id);
      if (status === 200 && data) {
        await createFilesToExport(t, [data], 'xlsx', 'order');
      } else {
        toast.error(t('mensajeerror'), { theme: 'colored' });
      }
    } catch (err) {
      toast.error(t('mensajeerror'), { theme: 'colored' });
    } finally {
      setExportingOrderId(null);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [t, page, selectedYear]);

  const handleReturn = async (orderId, articleIds, orderStatus) => {
    try {
      const shouldUpdateProductState = orderStatus?.id === 'processing' || orderStatus?.id === 'pending';
      if (shouldUpdateProductState && articleIds.length > 0 && selectedOrderDetails) {
        const allItems = selectedOrderDetails.articles.map((article) => {
          const isSelected = articleIds.includes(article.id);
          return {
            product: article.product,
            price: parseFloat(article.precio_venta || 0),
            quantity: parseInt(article.quantity || 1),
            state: isSelected ? 6 : parseInt(article.estado || 1),
            discount: parseInt(article.descuento || 0),
            total_price: parseFloat(article.precio_venta || 0) * parseInt(article.quantity || 1),
          };
        });
        const descuento = selectedOrderDetails.descuento || 0;
        const shippingCost = parseFloat(selectedOrderDetails.shipping_cost || 0);
        const subtotalOrder = getSubTotalOrder(allItems, descuento);
        const ivaOrder = getIvaOrder(allItems, descuento);
        const totalOrder = getTotalOrder(allItems, descuento, shippingCost);
        const updatePayload = {
          items: allItems.map((item) => ({
            id: item.product.id,
            quantity: parseInt(item.quantity),
            total_price: parseFloat(item.total_price),
            state: parseInt(item.state),
            discount: parseInt(item.discount),
          })),
          subtotal_order: parseFloat(subtotalOrder),
          iva_order: parseFloat(ivaOrder),
          total_order: parseFloat(totalOrder),
        };

        await editDetailOrder(updatePayload, orderId);
        
        const { data, status } = await getDetailOrder(orderId);
        if (status === 200) {
          setSelectedOrderDetails({ id: orderId, ...data });
        }
        
        setSelectedArticles([]);
        await fetchOrders();
        toast.success(t('productAnuladoCorrectamente'));
        return;
      }

      const payload = {
        pedido: orderId,
        return_status: 0,
        items: articleIds.map((id) => ({ articulo: id })),
      };
      const response = await createReturn(payload);
      setSelectedArticles([]);
      await fetchOrders();
      toast.success(t('refundRequestSuccessfully'));
    } catch (error) {
      console.error('Error:', error);
      toast.error(`${t('error')}: ${t('mensajeerror')}`);
    }
  };

  const handleToggleDetails = async (orderId) => {
    if (selectedOrderDetails?.id === orderId) {
      setSelectedOrderDetails(null);
      setSelectedArticles([]);
    } else {
      setSelectedArticles([]);
      try {
        const { data, status } = await getDetailOrder(orderId);
        if (status === 200) {
          setSelectedOrderDetails({ id: orderId, ...data });
          setErrorMessage('');
        } else {
          setErrorMessage(t('mensajeerror'));
        }
      } catch (error) {
        setErrorMessage(t('mensajeerror'));
      }
    }
  };

  const renderHeader = () => {
    return <span className="fw-bold">{minYear !== maxYear ? `${minYear} - ${maxYear}` : `${maxYear}`}</span>;
  };

  const handleSelectedYear = (date) => {
    setSelectedYear(date.getFullYear());
    setPage(1);
  };

  const handleClean = () => {
    setSelectedYear(null);
    setPage(1);
  };

  const handleSaveOrderChanges = async (changes) => {
    if (!changes || !selectedOrderDetails) {
      setShowAddItemsModal(false);
      return;
    }

    setIsSaving(true);
    try {
      const allItems = [];
      
      changes.existingProducts?.forEach((item) => {
        const article = item.article;
        const updatedQuantity = item.quantity;
        
        if (updatedQuantity > 0 && article) {
          allItems.push({
            product: article.product,
            price: parseFloat(article.precio_venta || 0),
            quantity: updatedQuantity,
            state: article.estado || 1,
            discount: article.descuento || 0,
            total_price: parseFloat(article.precio_venta || 0) * updatedQuantity,
          });
        }
      });
      
      changes.newProducts?.forEach((item) => {
        const product = item.product;
        if (product) {
          allItems.push({
            product: product,
            price: parseFloat(product.price || 0),
            quantity: item.quantity || 1,
            state: 1, 
            discount: 0,
            total_price: parseFloat(product.price || 0) * (item.quantity || 1),
          });
        }
      });
      
      const descuento = selectedOrderDetails.descuento || 0;
      const shippingCost = parseFloat(selectedOrderDetails.shipping_cost || 0);
      
      const subtotalOrder = getSubTotalOrder(allItems, descuento);
      const ivaOrder = getIvaOrder(allItems, descuento);
      const totalOrder = getTotalOrder(allItems, descuento, shippingCost);
      
      const payload = {
        items: allItems.map((item) => ({
          id: item.product.id,
          quantity: parseInt(item.quantity),
          total_price: parseFloat(item.total_price),
          state: parseInt(item.state),
          discount: parseInt(item.discount),
        })),
        subtotal_order: parseFloat(subtotalOrder),
        iva_order: parseFloat(ivaOrder),
        total_order: parseFloat(totalOrder),
      };
      
      const { status } = await editDetailOrder(payload, selectedOrderDetails.id);
      
      if (status === 200 || status === 201) {
        toast.success(t('editadocorrectamente'), {
          className: 'toast-success-white',
        });
        const { data, status: detailStatus } = await getDetailOrder(selectedOrderDetails.id);
        if (detailStatus === 200) {
          setSelectedOrderDetails({ id: selectedOrderDetails.id, ...data });
        }
        await fetchOrders();
      } else {
        toast.error(`${t('error')}: ${t('mensajeerror')}`);
      }
    } catch (error) {
      console.error('Error al guardar cambios:', error);
      toast.error(`${t('error')}: ${t('mensajeerror')}`);
    } finally {
      setIsSaving(false);
      setShowAddItemsModal(false);
    }
  };

  useEffect(() => {
    const getMinYear = async () => {
      const year = await getMinYearOrder();
      if (year.status === 200) setMinYear(year.data?.min_year);
      else setMinYear(maxYear);
    };
    getMinYear();
  }, []);

  return (
    <Container fluid className="margin-top-md mx-3 d-flex mainDiv order-list">
      <Head title={title} noIndex={true} />
      <Row className="w-100">
        <Col md={12} className="d-flex flex-row justify-content-between datetimepicker-userzone">
          <h1 className="page-title static-title d-flex justify-content-start">{t('pedidos')}</h1>
          <div className="d-flex justify-content-end gap-3">
            <DatePicker
              className="d-flex justify-content-end form-control"
              value={selectedYear ? new Date(selectedYear, 0, 1) : ''}
              onChange={handleSelectedYear}
              dateFormat="yyyy"
              showYearPicker={true}
              showTimeInput={false}
              placeholderText={t('filterByYear')}
              showIcon={true}
              icon={<BiCalendarCheck />}
              minDate={new Date(minYear, 0, 1)}
              maxDate={new Date(maxYear, 11, 31)}
              renderCustomHeader={renderHeader}
            />
            <div className="close-search mt-2">
              <PiBroomBold title={t('clean')} onClick={handleClean} />
            </div>
          </div>
        </Col>
        {errorMessage ? (
          <Col md={12}>
            <div className="text-center">
              <img className="mb-1" src={error} alt="error.png" />
              <h2 className="mb-1">{errorMessage}</h2>
              <Link to="/" className="mt-4 d-block">
                <Button className="p-3">{t('gobackhome', { ns: 'home' }).toUpperCase()}</Button>
              </Link>
            </div>
          </Col>
        ) : isLoadingOrders ? (
          <Col md={12} className="d-flex justify-content-center align-items-center py-5">
            <Spinner animation="border" role="status" variant="primary">
              <span className="visually-hidden">{t('cargando', { defaultValue: 'Cargando...' })}</span>
            </Spinner>
          </Col>
        ) : orders.length > 0 ? (
          <>
            <Col md={12} className="mb-2 d-none d-md-block">
              <Card className="card-headers">
                <Row>
                  <Col
                    md={1}
                    className="d-flex flex-column align-items-center justify-content-center text-center text-md-left mb-3 mb-md-0"
                  >
                    <Card.Title>
                      <strong className="txt-headers">{t('promoCodes.code')}</strong>
                    </Card.Title>
                  </Col>
                  <Col
                    md={1}
                    className="d-flex flex-column align-items-center justify-content-center text-center text-md-left mb-3 mb-md-0"
                  >
                    <Card.Text>
                      <strong className="txt-headers">{t('date')}</strong>
                    </Card.Text>
                  </Col>
                  <Col
                    md={2}
                    className="d-flex flex-column align-items-center justify-content-center text-center text-md-left mb-3 mb-md-0"
                  >
                    <Card.Text>
                      <strong className="txt-headers">{t('orderTrans.paymentMethod')}</strong>
                    </Card.Text>
                  </Col>
                  <Col
                    md={2}
                    className="d-flex flex-column align-items-center justify-content-center text-center text-md-left mb-3 mb-md-0"
                  >
                    <Card.Text>
                      <strong className="txt-headers">{t('orderTrans.paymentStatus')}</strong>
                    </Card.Text>
                  </Col>
                  <Col
                    md={2}
                    className="d-flex flex-column align-items-center justify-content-center text-center text-md-left mb-3 mb-md-0"
                  >
                    <Card.Text>
                      <strong className="txt-headers">{t('orderTrans.orderStatus')}</strong>
                    </Card.Text>
                  </Col>
                  <Col
                    md={2}
                    className="d-flex flex-column align-items-center justify-content-center text-center text-md-left mb-3 mb-md-0"
                  >
                    <Card.Text>
                      <strong className="txt-headers">{t('product.totalPrice')}</strong>
                    </Card.Text>
                  </Col>
                  <Col
                    md={2}
                    className="d-flex flex-column align-items-end justify-content-center text-center text-md-right mb-3 mb-md-0"
                  >
                    <Card.Text className="px-1">
                      <strong className="txt-headers">{t('orderDetails')}</strong>
                    </Card.Text>
                  </Col>
                </Row>
              </Card>
            </Col>
            {orders.map((order) => (
              <Col md={12} key={order.id} className="mb-2">
                <Card>
                  <Row>
                    <Col
                      md={1}
                      className="d-flex flex-column align-items-center justify-content-center text-center text-md-left mb-3 mb-md-0"
                    >
                      <div className="d-none d-md-block">
                        <Card.Title className="mb-0">#{order.code}</Card.Title>
                      </div>
                      <div className="d-md-none text-center">
                        <Card.Title>
                          {t('promoCodes.code')}: #{order.code}
                        </Card.Title>
                      </div>
                    </Col>
                    <Col
                      md={1}
                      className="d-flex flex-column align-items-center justify-content-center text-center text-md-left mb-3 mb-md-0"
                    >
                      <div className="d-none d-md-block">
                        <Card.Text className="mb-0">{convertDateToDDMMYYYY(order.fecha)}</Card.Text>
                      </div>
                      <div className="d-md-none text-center">
                        <Card.Text>
                          <strong>
                            {t('date')}: {convertDateToDDMMYYYY(order.fecha)}
                          </strong>
                        </Card.Text>
                      </div>
                    </Col>
                    <Col
                      md={2}
                      className="d-flex flex-column align-items-center justify-content-center text-center text-md-left mb-3 mb-md-0"
                    >
                      <div className="d-none d-md-block">
                        <Card.Text className="mb-0">{order.payment_method}</Card.Text>
                      </div>
                      <div className="d-md-none text-center">
                        <Card.Text>
                          <strong>
                            {t('state')}: {order.payment_method}
                          </strong>
                        </Card.Text>
                      </div>
                    </Col>
                    <Col
                      md={2}
                      className="d-flex flex-column align-items-center justify-content-center text-center text-md-left mb-3 mb-md-0"
                    >
                      <div className="d-none d-md-block">
                        <Card.Text className="mb-0">{order.payment_status_display}</Card.Text>
                      </div>
                      <div className="d-md-none text-center">
                        <Card.Text>
                          <strong>
                            {t('state')}: {order.payment_status_display}
                          </strong>
                        </Card.Text>
                      </div>
                    </Col>
                    <Col
                      md={2}
                      className="d-flex flex-column align-items-center justify-content-center text-center text-md-left mb-3 mb-md-0"
                    >
                      <div className="d-none d-md-block">
                        <Card.Text className="mb-0">{t(order.status.text)}</Card.Text>
                      </div>
                      <div className="d-md-none text-center">
                        <Card.Text>
                          <strong>
                            {t('state')}: {t(order.status.text)}
                          </strong>
                        </Card.Text>
                      </div>
                    </Col>
                    <Col
                      md={2}
                      className="d-flex flex-column align-items-center justify-content-center text-center text-md-left mb-3 mb-md-0"
                    >
                      <div className="d-none d-md-block">
                        <Card.Text className="mb-0">{formatCurrency(order.total_order)}</Card.Text>
                      </div>
                      <div className="d-md-none text-center">
                        <Card.Text>
                          <strong>
                            {t('product.totalPrice')}: {formatCurrency(order.total_order)}
                          </strong>
                        </Card.Text>
                      </div>
                    </Col>
                    <Col
                      md={2}
                      className="d-flex flex-column align-items-center align-items-md-end justify-content-center text-center text-md-left mb-3 mb-md-0 col-md-2"
                    >
                      <div className="d-flex gap-2 flex-wrap justify-content-center justify-content-md-end">
                        {shouldShowPayOrderButton(order, isConcourseUser) && (
                          <Button
                            className="button-secondary card-button btn-secondary"
                            title={t('payOrder', { ns: 'payment' })}
                            disabled={payingOrderId === order.id}
                            onClick={() => handlePayOrder(order)}
                          >
                            {payingOrderId === order.id ? (
                              <Spinner animation="border" size="sm" />
                            ) : (
                              <BsCreditCard className="icon-md" />
                            )}
                          </Button>
                        )}
                        <Button
                          className="button-secondary card-button btn-secondary"
                          title={t('verDetalle', { defaultValue: 'Ver detalle' })}
                          onClick={() => handleToggleDetails(order.id)}
                        >
                          {selectedOrderDetails?.id === order.id ? (
                            <AiFillEyeInvisible className="icon-md" />
                          ) : (
                            <AiFillEye className="icon-md" />
                          )}
                        </Button>
                        <Button
                          className="button-secondary card-button btn-secondary"
                          title={t('exportarDetalle', { defaultValue: 'Exportar detalle' })}
                          onClick={() => handleExportOrder(order)}
                          disabled={exportingOrderId === order.id}
                        >
                          {exportingOrderId === order.id ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <FiDownload className="icon-md" />
                          )}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                  {selectedOrderDetails?.id === order.id && (
                    <Row className="mt-4">
                      {selectedOrderDetails?.articles?.length > 0 ? (
                        <>
                          <h5 className="mb-3 d-md-none text-center">{t('productos')}</h5>
                          <Col md={12} className="mb-2 d-none d-md-block">
                            <Card className="card-productos-container">
                              <Row className="product-header-row">
                                <Col md={2} className="d-flex flex-column justify-content-center mb-3 mb-md-0">
                                  <Card.Text>
                                    <strong className="txt-headers">{t('productos')}</strong>
                                  </Card.Text>
                                </Col>
                                <Col
                                  md={1}
                                  className="d-flex flex-column align-items-center justify-content-center text-center text-md-left mb-3 mb-md-0"
                                >
                                  <Card.Text>
                                    <strong className="txt-headers">{t('product.editorial')}</strong>
                                  </Card.Text>
                                </Col>
                                <Col
                                  md={1}
                                  className="d-flex flex-column align-items-center justify-content-center text-center text-md-left mb-3 mb-md-0"
                                >
                                  <Card.Text>
                                    <strong className="txt-headers">{t('product.quantity')}</strong>
                                  </Card.Text>
                                </Col>
                                <Col
                                  md={1}
                                  className="d-flex flex-column align-items-center justify-content-center text-center text-md-left mb-3 mb-md-0"
                                >
                                  <Card.Text>
                                    <strong className="txt-headers">
                                      {t(orderLinesConditionColumnI18nKey(selectedOrderDetails?.articles), { ns: 'detalle' })}
                                    </strong>
                                  </Card.Text>
                                </Col>
                                <Col
                                  md={1}
                                  className="d-flex flex-column align-items-center justify-content-center text-center text-md-left mb-3 mb-md-0"
                                >
                                  <Card.Text>
                                    <strong className="txt-headers">{t('orderTrans.paymentStatus')}</strong>
                                  </Card.Text>
                                </Col>
                                <Col
                                  md={1}
                                  className="d-flex flex-column align-items-center justify-content-center text-center text-md-left mb-3 mb-md-0"
                                >
                                  <Card.Text>
                                    <strong className="txt-headers">{t('orderTrans.orderStatus')}</strong>
                                  </Card.Text>
                                </Col>
                                <Col
                                  md={1}
                                  className="d-flex flex-column align-items-center justify-content-center text-center text-md-left mb-3 mb-md-0"
                                >
                                  <Card.Text>
                                    <strong className="txt-headers">{t('orderTrans.shippingStatus', { defaultValue: 'Estado envío' })}</strong>
                                  </Card.Text>
                                </Col>
                                <Col
                                  md={1}
                                  className="d-flex flex-column align-items-center justify-content-center text-center text-md-left mb-3 mb-md-0"
                                >
                                  <Card.Text>
                                    <strong className="txt-headers">{t('orderTrans.transporter')}</strong>
                                  </Card.Text>
                                </Col>
                                <Col
                                  md={1}
                                  className="d-flex flex-column align-items-center justify-content-center text-center text-md-left mb-3 mb-md-0"
                                >
                                  <Card.Text>
                                    <strong className="txt-headers">{t('orderTrans.tracking')}</strong>
                                  </Card.Text>
                                </Col>
                                <Col
                                  md={2}
                                  className="d-flex flex-column align-items-center justify-content-center mb-3 mb-md-0"
                                >
                                  <div className="d-flex gap-2 flex-wrap justify-content-center justify-content-md-end">
                                    <Button
                                      className="btn-return"
                                      variant="danger"
                                      size="sm"
                                      disabled={selectedArticles.length === 0}
                                      onClick={() => handleReturn(order.id, selectedArticles, order.status)}
                                    >
                                      {t('cancelOrder')}
                                    </Button>
                                    <Button
                                      className="btn-add"
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={() => setShowAddItemsModal(true)}
                                    >
                                      <FaPlus className="me-1" />
                                      {t('add', { ns: 'backoffice' })}
                                    </Button>
                                  </div>
                                </Col>
                              </Row>
                              {selectedOrderDetails?.articles?.map((item, index) => (
                                <Row key={`${item.product.id}-${index}`} className="product-row">
                                  <Col
                                    md={2}
                                    className="container-detail-product text-md-left mb-3 mb-md-0 justify-content-center justify-content-md-start"
                                  >
                                    <img
                                      className="card-img"
                                      src={item.product.cover || preview}
                                      alt={item.product.editorial}
                                    />
                                    <Card.Title className="mb-0">
                                      <Link
                                        to={libraryPath(`libro/${item.product.id}`)}
                                        className="title-product"
                                      >
                                        {item.product.title}
                                      </Link>
                                    </Card.Title>
                                  </Col>
                                  <Col md={1} className="d-flex flex-column align-items-center justify-content-center mb-3 mb-md-0">
                                    <div className="d-none d-md-flex h-100 align-items-center justify-content-center">
                                      <div className="d-flex flex-column justify-content-center">
                                        <Card.Text className="mb-0 text-center">{item.product.editorial}</Card.Text>
                                      </div>
                                    </div>
                                  </Col>
                                  <Col
                                    md={1}
                                    className="d-flex flex-column justify-content-center align-items-center mb-3 mb-md-0"
                                  >
                                    <Card.Text className="d-none d-md-flex mb-0">{item.quantity}</Card.Text>
                                  </Col>
                                  <Col
                                    md={1}
                                    className="d-flex flex-column justify-content-center align-items-center mb-3 mb-md-0"
                                  >
                                    <Card.Text className="d-none d-md-flex mb-0 text-center text-md-left">
                                      {formatArticleBookCondition(item)}
                                    </Card.Text>
                                  </Col>
                                  <Col
                                    md={1}
                                    className="d-flex flex-column justify-content-center align-items-center mb-3 mb-md-0"
                                  >
                                    <Card.Text className="d-none d-md-flex mb-0 text-center">
                                      {order.payment_status_display || '-'}
                                    </Card.Text>
                                  </Col>
                                  <Col
                                    md={1}
                                    className="d-flex flex-column justify-content-center align-items-center mb-3 mb-md-0"
                                  >
                                    <Card.Text className="d-none d-md-flex mb-0 text-center">
                                      {order.status?.text ? t(order.status.text) : '-'}
                                    </Card.Text>
                                  </Col>
                                  <Col
                                    md={1}
                                    className="d-flex flex-column justify-content-center align-items-center mb-3 mb-md-0"
                                  >
                                    <div className="d-none d-md-flex">
                                      <Badge 
                                        className={`shipping-status-badge ${Number(item.estado) === 6 ? 'shipping-status-badge--voided' : ''}`}
                                      >
                                        {(() => {
                                          if (!articleStatus || articleStatus.length === 0) return '-';
                                          const estadoNum = Number(item.estado);
                                          const status = articleStatus.find(s => s.id === estadoNum);
                                          return status ? status.value : '-';
                                        })()}
                                      </Badge>
                                    </div>
                                  </Col>
                                  <Col
                                    md={1}
                                    className="d-flex flex-column justify-content-center align-items-center mb-3 mb-md-0"
                                  >
                                    <Card.Text className="d-none d-md-flex mb-0">
                                      {selectedOrderDetails?.transport_company?.name || selectedOrderDetails?.transport_company || '-'}
                                    </Card.Text>
                                  </Col>
                                  <Col
                                    md={1}
                                    className="d-flex flex-column justify-content-center align-items-center mb-3 mb-md-0"
                                  >
                                    {selectedOrderDetails?.tracking_id && selectedOrderDetails?.transport_company?.url ? (
                                      <Card.Text className="d-none d-md-flex mb-0">
                                        <a
                                          href={selectedOrderDetails.transport_company.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="tracking-text"
                                          style={{ textDecoration: 'none' }}
                                        >
                                          {selectedOrderDetails.tracking_id}
                                        </a>
                                      </Card.Text>
                                    ) : (
                                      <Card.Text className="d-none d-md-flex mb-0 tracking-text">
                                        {selectedOrderDetails?.tracking_id || '-'}
                                      </Card.Text>
                                    )}
                                  </Col>
                                  <Col
                                    md={2}
                                    className="d-flex flex-column justify-content-center align-items-center mb-3 mb-md-0"
                                  >
                                    <div className="d-none d-md-flex align-items-center gap-2">
                                      <Card.Text className={`mb-0 price-text ${Number(item.estado) === 6 ? 'price-text--voided' : ''}`}>
                                        {renderPrice(item)}
                                      </Card.Text>
                                      {Number(item.estado) !== 6 && (
                                        <Form.Check
                                          type="checkbox"
                                          checked={selectedArticles.includes(item.id)}
                                          onChange={() => handleSelectArticle(item.id)}
                                        />
                                      )}
                                    </div>
                                  </Col>
                                </Row>
                              ))}
                            </Card>
                          </Col>
                          {selectedOrderDetails?.articles?.map((item, index) => (
                            <Col md={12} key={`${item.product.id}-${index}`} className="mb-2 d-md-none">
                              <Card className="card-internal">
                                <Row>
                                  <Col
                                    md={3}
                                    className="container-detail-product text-md-left mb-3 mb-md-0 justify-content-center justify-content-md-start"
                                  >
                                    <img
                                      className="card-img"
                                      src={item.product.cover || preview}
                                      alt={item.product.editorial}
                                    />
                                    <Card.Title className="mb-0">
                                      <Link
                                        to={libraryPath(`libro/${item.product.id}`)}
                                        className="title-product"
                                      >
                                        {item.product.title}
                                      </Link>
                                    </Card.Title>
                                  </Col>
                                  <Col md={1} className="d-flex flex-column align-items-center justify-content-center mb-3 mb-md-0">
                                    <div className="d-none d-md-flex h-100 align-items-center justify-content-center">
                                      <div className="d-flex flex-column justify-content-center">
                                        <Card.Text className="mb-0 text-center">{item.product.editorial}</Card.Text>
                                      </div>
                                    </div>
                                    <div className="d-md-none text-center">
                                      <Card.Text>
                                        <strong>
                                          {t('product.editorial')}: {item.product.editorial}
                                        </strong>
                                      </Card.Text>
                                    </div>
                                  </Col>
                                  <Col
                                    md={1}
                                    className="d-flex flex-column justify-content-center align-items-center mb-3 mb-md-0"
                                  >
                                    <Card.Text className="d-none d-md-flex mb-0">{item.quantity}</Card.Text>
                                    <div className="d-md-none text-center">
                                      <Card.Text>
                                        <strong>
                                          {t('product.quantity')}: {item.quantity}
                                        </strong>
                                      </Card.Text>
                                    </div>
                                  </Col>
                                  <Col xs={12} className="d-md-none text-center mb-2">
                                    <Card.Text className="mb-0">
                                      <strong>
                                        {t(isBookOrderLine(item) ? 'bookConditionColumn' : 'productConditionColumn', { ns: 'detalle' })}:{' '}
                                        {formatArticleBookCondition(item)}
                                      </strong>
                                    </Card.Text>
                                  </Col>
                                  <Col
                                    md={2}
                                    className="d-flex flex-column justify-content-center align-items-center mb-3 mb-md-0"
                                  >
                                    <div className="d-none d-md-flex">
                                      <Badge 
                                        className={`shipping-status-badge ${Number(item.estado) === 6 ? 'shipping-status-badge--voided' : ''}`}
                                      >
                                        {(() => {
                                          const status = articleStatus.find(s => s.id === Number(item.estado));
                                          return status ? status.value : '-';
                                        })()}
                                      </Badge>
                                    </div>
                                    <div className="d-md-none text-center">
                                      <Card.Text>
                                        <strong>
                                          {t('orderTrans.shippingStatus', { defaultValue: 'Estado envío' })}:{' '}
                                          <Badge 
                                            className={`shipping-status-badge ${Number(item.estado) === 6 ? 'shipping-status-badge--voided' : ''}`}
                                          >
                                            {(() => {
                                              if (!articleStatus || articleStatus.length === 0) return '-';
                                              const estadoNum = Number(item.estado);
                                              const status = articleStatus.find(s => s.id === estadoNum);
                                              return status ? status.value : '-';
                                            })()}
                                          </Badge>
                                        </strong>
                                      </Card.Text>
                                    </div>
                                  </Col>
                                  <Col
                                    md={2}
                                    className="d-flex flex-column justify-content-center align-items-center mb-3 mb-md-0"
                                  >
                                    <Card.Text className="d-none d-md-flex mb-0">
                                      {selectedOrderDetails?.transport_company?.name || selectedOrderDetails?.transport_company || '-'}
                                    </Card.Text>
                                    <div className="d-md-none text-center">
                                      <Card.Text>
                                        <strong>
                                          {t('orderTrans.shippingProvider')}: {selectedOrderDetails?.transport_company?.name || selectedOrderDetails?.transport_company || '-'}
                                        </strong>
                                      </Card.Text>
                                    </div>
                                  </Col>
                                  <Col
                                    md={2}
                                    className="d-flex flex-column justify-content-center align-items-center mb-3 mb-md-0"
                                  >
                                    <Card.Text className="d-none d-md-flex mb-0 tracking-text">
                                      {selectedOrderDetails?.tracking_id || '-'}
                                    </Card.Text>
                                    <div className="d-md-none text-center">
                                      <Card.Text>
                                        <strong>
                                          {t('orderTrans.trackingId')}:{' '}
                                          {selectedOrderDetails?.tracking_id && selectedOrderDetails?.transport_company?.url ? (
                                            <a
                                              href={selectedOrderDetails.transport_company.url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="tracking-text"
                                              style={{ textDecoration: 'none' }}
                                            >
                                              {selectedOrderDetails.tracking_id}
                                            </a>
                                          ) : (
                                            <span className="tracking-text">{selectedOrderDetails?.tracking_id || '-'}</span>
                                          )}
                                        </strong>
                                      </Card.Text>
                                    </div>
                                  </Col>
                                  <Col
                                    md={1}
                                    className="d-flex flex-column justify-content-center align-items-center mb-3 mb-md-0"
                                  >
                                    <div className="d-none d-md-flex align-items-center gap-2">
                                      <Card.Text className={`mb-0 price-text ${Number(item.estado) === 6 ? 'price-text--voided' : ''}`}>
                                        {renderPrice(item)}
                                      </Card.Text>
                                      {Number(item.estado) !== 6 && (
                                        <Form.Check
                                          type="checkbox"
                                          checked={selectedArticles.includes(item.id)}
                                          onChange={() => handleSelectArticle(item.id)}
                                        />
                                      )}
                                    </div>
                                    <div className="d-md-none text-center">
                                      <Card.Text>
                                        <strong className={`price-text ${Number(item.estado) === 6 ? 'price-text--voided' : ''}`}>
                                          {t('price')}: {renderPrice(item)}
                                        </strong>
                                      </Card.Text>
                                      {Number(item.estado) !== 6 && (
                                        <Form.Check
                                          type="checkbox"
                                          className="mb-3"
                                          checked={selectedArticles.includes(item.id)}
                                          onChange={() => handleSelectArticle(item.id)}
                                        />
                                      )}
                                    </div>
                                  </Col>
                                </Row>
                              </Card>
                            </Col>
                          ))}
                        </>
                      ) : (
                        <Card className="card-productos">
                          <Row>
                            <Col md={12} className="text-center">
                              <Card.Title>
                                <strong className="txt-headers">{t('noproducts', { ns: 'header' })}</strong>
                              </Card.Title>
                            </Col>
                          </Row>
                        </Card>
                      )}
                    </Row>
                  )}
                </Card>
              </Col>
            ))}
            <Col md={12}>
              <Pagination changePage={setPage} page={page} totalPages={maxPage} />
            </Col>
          </>
        ) : (
          <Col md={12}>
            <NoResults className="noFavorites" />
          </Col>
        )}
      </Row>
      <AddItemsToOrderModal
        show={showAddItemsModal}
        onHide={handleSaveOrderChanges}
        order={selectedOrderDetails}
        articles={selectedOrderDetails?.articles || []}
        loading={isSaving}
      />
    </Container>
  );
};

export default OrderList;
